import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { IMAGES } from "@/content/site";
import { useLang } from "@/lang";

const EASE = [0.22, 1, 0.36, 1];
const rise = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay: i * 0.12 },
  }),
};

export default function Problem() {
  const { t, isEs } = useLang();
  const rm = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], rm ? [0, 0] : [-36, 36]);
  const lang = isEs ? "es" : "en";
  const vw = { once: true, amount: 0.4 };

  return (
    <section id="problem" data-testid="problem-section" ref={ref} className="relative overflow-hidden py-20 sm:py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div>
          <motion.div variants={rise} custom={0} initial="hidden" whileInView="show" viewport={vw} className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-gold" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.problem.eyebrow}</p>
          </motion.div>

          <h2 className="font-serif text-4xl font-medium leading-[1.08] text-navy sm:text-5xl lg:text-6xl">
            {t.problem.lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  variants={rise}
                  custom={i + 1}
                  initial="hidden"
                  whileInView="show"
                  viewport={vw}
                  className={`block ${i === 2 ? "italic text-gold" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h2>

          <motion.p variants={rise} custom={4} initial="hidden" whileInView="show" viewport={vw} className="mt-8 max-w-xl font-serif text-xl italic leading-relaxed text-navy/90 sm:text-2xl">
            {t.problem.lead}
          </motion.p>
          <motion.p variants={rise} custom={5} initial="hidden" whileInView="show" viewport={vw} className="mt-5 max-w-xl text-base leading-relaxed text-mutedink">
            {t.problem.body}
          </motion.p>

          <motion.div variants={rise} custom={6} initial="hidden" whileInView="show" viewport={vw} className="mt-8 flex flex-wrap gap-3">
            {t.problem.chips.map((c) => (
              <span key={c} className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy">
                <Check className="h-3.5 w-3.5 text-gold" />
                {c}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div aria-hidden="true" className="absolute inset-0 translate-x-4 translate-y-4 rounded-b-3xl rounded-t-[999px] border border-gold/50" />
          <div className="relative overflow-hidden rounded-b-3xl rounded-t-[999px]">
            <motion.img
              style={{ y: yImg }}
              src={IMAGES.problem.src}
              alt={IMAGES.problem.alt[lang]}
              loading="lazy"
              className="h-[420px] w-full scale-110 object-cover sm:h-[520px]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
